import { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { Send } from 'lucide-react'
import { useSynapseWorker } from '../../hooks/useSynapseWorker'
import type { Task as WorkerTask } from '../../types/worker-messages'
import './AssemblyLine.css'

// ============================================================================
// TYPES
// ============================================================================

export interface Task {
  id: string
  task_id?: string
  name?: string
  description?: string
  status: string
  phase?: string
  progress: number
  timestamp?: number
  created_at?: string
  completed_at?: string
  error?: string | null
}

interface TaskStatus {
  task_id: string
  status: string
  progress: number
  phase: string
  tokens_used: number
  lines_generated: number
  output_preview: string
  files_modified: string[]
  started_at: string | null
  elapsed_seconds: number | null
}

interface StageGroup {
  plan: Task[]
  build: Task[]
  review: Task[]
  complete: Task[]
}

type StageName = keyof StageGroup

interface TransitioningTask {
  task: Task
  fromStage: StageName
  toStage: StageName
  startTime: number
}

export interface AssemblyLineProps {
  isRunning?: boolean
  refreshInterval?: number
  demoMode?: boolean
  onTaskSelect?: (taskId: string) => void
  compact?: boolean  // Minimal mode - no input, smaller
}

// ============================================================================
// CONSTANTS
// ============================================================================

const LEGACY_STAGES: { key: StageName; label: string; icon: string; color: string }[] = [
  { key: 'plan', label: 'PLAN', icon: '📋', color: '#00ff88' },
  { key: 'build', label: 'BUILD', icon: '⚙️', color: '#00d4ff' },
  { key: 'review', label: 'REVIEW', icon: '🔍', color: '#f59e0b' },
  { key: 'complete', label: 'COMPLETE', icon: '✅', color: '#ff00ff' },
]

const POLI_STAGES: { key: StageName; label: string; icon: string; color: string }[] = [
  { key: 'plan', label: 'PLAN', icon: '📋', color: '#00ff88' },
  { key: 'build', label: 'OPTIMIZE', icon: '🎯', color: '#00d4ff' },
  { key: 'review', label: 'LAYOUT', icon: '📐', color: '#f59e0b' },
  { key: 'complete', label: 'IMPLEMENT', icon: '⚡', color: '#ff00ff' },
]

const STAGE_INDEX: Record<StageName, number> = { plan: 0, build: 1, review: 2, complete: 3 }

// File type icons/labels
const FILE_TYPES = ['tsx', 'css', 'ts', 'json', 'py', 'rs', 'html', 'md'] as const
type FileType = typeof FILE_TYPES[number]

const FILE_TYPE_COLORS: Record<FileType, string> = {
  tsx: '#61dafb',
  css: '#264de4',
  ts: '#3178c6',
  json: '#cbcb41',
  py: '#3776ab',
  rs: '#dea584',
  html: '#e34c26',
  md: '#083fa1',
}

interface DemoTask extends Task {
  fileType?: FileType
}

const TRANSITION_DURATION = 600 // ms

// ============================================================================
// HELPERS
// ============================================================================

function getTaskName(task: Task): string {
  return task.name || task.description || task.task_id?.slice(0, 8) || task.id?.slice(0, 8) || 'Task'
}

function getTaskId(task: Task): string {
  return task.task_id || task.id
}

function mapToStage(task: Task): StageName {
  const status = (task.status || '').toUpperCase()
  const phase = (task.phase || '').toLowerCase()

  // POLI PHASES - Check these FIRST before legacy logic
  // IMPLEMENT phase → 'complete' column
  if (phase === 'implement' || phase === 'implementing' || phase.startsWith('implement_')) {
    return 'complete'
  }

  // LAYOUT phase → 'review' column
  if (phase === 'layout' || phase === 'layouting' || phase.startsWith('layout_')) {
    return 'review'
  }

  // OPTIMIZE phase → 'build' column
  if (phase === 'optimize' || phase === 'optimizing' || phase.startsWith('optimize_')) {
    return 'build'
  }

  // PLAN phase (both POLI and legacy) → 'plan' column
  if (phase === 'plan' || phase === 'planning' || phase.startsWith('plan_')) {
    return 'plan'
  }

  // COMPLETE: Task fully done (status=COMPLETED, phase=deployed/vaulted/build_only)
  if (status === 'COMPLETED' || status === 'VAULTED' ||
      phase === 'deployed' || phase === 'vaulted' || phase === 'build_only' ||
      phase === 'ready' || phase === 'complete') {
    return 'complete'
  }

  // REVIEW: QA-related statuses/phases - force to review column
  if (status === 'QA' || status === 'QA_FAILED' || status === 'QA_PENDING' ||
      phase === 'qa' || phase === 'qa_failed' || phase === 'qa_pending' ||
      phase === 'review' || phase === 'awaiting_review') {
    return 'review'
  }

  // BUILD: All active work (executing, building, vault_index, auto_deploy)
  if (phase.startsWith('executing') || phase === 'building' || phase === 'build' ||
      phase === 'vault_index' || phase === 'auto_deploy' ||
      phase === 'build_error' || status === 'PROCESSING') {
    return 'build'
  }

  // PLAN: Queued, pending, or actively planning
  if (status === 'QUEUED' || status === 'PLAN' || status === 'PENDING' ||
      phase === 'pending' || phase === 'queued') {
    return 'plan'
  }

  // Default fallback to plan for unknown states
  return 'plan'
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function AssemblyLine({
  isRunning = true,
  refreshInterval = 10000, // 10s - reduced from 2s to prevent API hammering
  demoMode = false,
  onTaskSelect,
  compact = false
}: AssemblyLineProps = {}) {
  const [tasks, setTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null)
  const [taskOutput, setTaskOutput] = useState<string>('')
  const [taskStatus, setTaskStatus] = useState<TaskStatus | null>(null)
  const [liveLogsEnabled, setLiveLogsEnabled] = useState(false)
  const [transitioningTasks, setTransitioningTasks] = useState<Map<string, TransitioningTask>>(new Map())
  const [justLandedIds, setJustLandedIds] = useState<Set<string>>(new Set())

  // Inline task input
  const [taskInput, setTaskInput] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [pipelineMode, setPipelineMode] = useState<'legacy' | 'poli'>('poli')

  // Worker shell integration
  const { api: synapseApi, isReady: workerReady, isConnected: wsConnected } = useSynapseWorker()

  const handleSubmitTask = useCallback(async () => {
    if (!taskInput.trim() || isSubmitting || !workerReady) return
    setIsSubmitting(true)
    try {
      // Use worker shell instead of direct fetch
      await synapseApi.submitTask(taskInput.trim(), 'NORMAL')
      setTaskInput('')
      fetchTasks() // Refresh immediately
    } catch (e) {
      console.error('Task submit failed:', e)
    } finally {
      setIsSubmitting(false)
    }
  }, [taskInput, isSubmitting, workerReady, synapseApi])

  const fetchIntervalRef = useRef<NodeJS.Timeout>()
  const prevTaskStagesRef = useRef<Map<string, StageName>>(new Map())
  const stageRefs = useRef<Map<StageName, HTMLDivElement | null>>(new Map())

  // Select stage configuration based on mode
  const STAGES = useMemo(() => pipelineMode === 'poli' ? POLI_STAGES : LEGACY_STAGES, [pipelineMode])

  // Fetch tasks from API using worker shell
  const fetchTasks = useCallback(async () => {
    if (!workerReady) {
      setLoading(true)
      return
    }
    try {
      // Use worker shell instead of direct fetch
      const taskArray = await synapseApi.listTasks()

      // Detect stage transitions
      const now = Date.now()
      const newTransitions = new Map(transitioningTasks)

      taskArray.forEach((task: Task) => {
        const id = getTaskId(task)
        const newStage = mapToStage(task)
        const prevStage = prevTaskStagesRef.current.get(id)

        if (prevStage && prevStage !== newStage) {
          // Task changed stage - add to transitioning
          newTransitions.set(id, {
            task,
            fromStage: prevStage,
            toStage: newStage,
            startTime: now
          })

          // Remove from transitioning after animation
          setTimeout(() => {
            setTransitioningTasks(prev => {
              const next = new Map(prev)
              next.delete(id)
              return next
            })
          }, TRANSITION_DURATION)
        }

        prevTaskStagesRef.current.set(id, newStage)
      })

      if (newTransitions.size !== transitioningTasks.size) {
        setTransitioningTasks(newTransitions)
      }

      setTasks(taskArray)
      setError(null)
      setLoading(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch')
      setLoading(false)
    }
  }, [workerReady, synapseApi, transitioningTasks])

  // Only poll when WS disconnected - WS subscription handles live updates
  useEffect(() => {
    fetchTasks()
    // Only poll as fallback when WebSocket is not connected
    if (!wsConnected) {
      fetchIntervalRef.current = setInterval(fetchTasks, Math.max(refreshInterval, 5000)) // Min 5s fallback
    }
    return () => { if (fetchIntervalRef.current) clearInterval(fetchIntervalRef.current) }
  }, [fetchTasks, refreshInterval, wsConnected])

  // WebSocket live log streaming for expanded task
  useEffect(() => {
    if (!expandedTaskId || !liveLogsEnabled) return

    const wsUrl = `ws://localhost:3003/tasks/${expandedTaskId}/logs`
    const ws = new WebSocket(wsUrl)

    ws.onopen = () => {
      console.log('[LiveLogs] Connected to task log stream:', expandedTaskId)
    }

    ws.onmessage = (event) => {
      try {
        const logLine = JSON.parse(event.data)
        setTaskOutput(prev => prev + '\n' + (logLine.message || logLine.line || event.data))
      } catch {
        setTaskOutput(prev => prev + '\n' + event.data)
      }
    }

    ws.onerror = (error) => {
      console.error('[LiveLogs] WebSocket error:', error)
    }

    ws.onclose = () => {
      console.log('[LiveLogs] Disconnected from task log stream')
    }

    return () => {
      ws.close()
    }
  }, [expandedTaskId, liveLogsEnabled])

  // Group tasks by stage (excluding transitioning tasks from their old stage)
  const stages = useMemo(() => {
    const groups: StageGroup = { plan: [], build: [], review: [], complete: [] }
    const transitioningIds = new Set(transitioningTasks.keys())

    tasks.forEach(task => {
      const id = getTaskId(task)
      // Skip tasks that are currently transitioning
      if (transitioningIds.has(id)) return
      groups[mapToStage(task)].push(task)
    })
    return groups
  }, [tasks, transitioningTasks])

  const handleTaskClick = useCallback(async (taskId: string) => {
    setSelectedTaskId(taskId)
    setExpandedTaskId(taskId)
    onTaskSelect?.(taskId)

    if (!workerReady) {
      setTaskOutput('Worker not ready')
      return
    }

    // Fetch task output and status via worker shell
    try {
      const taskData = await synapseApi.getTask(taskId)
      setTaskOutput(taskData.output || taskData.task.output || taskData.task.code_output || taskData.task.error || 'No output available')
      if (taskData.status_details) {
        setTaskStatus(taskData.status_details)
      }
    } catch (e) {
      console.error('Failed to fetch task:', e)
      setTaskOutput('Failed to load output')
      setTaskStatus(null)
    }
  }, [onTaskSelect, workerReady, synapseApi])


  // Get status badge info
  const getStatusBadge = (task: Task): { label: string; color: string } => {
    const status = (task.status || '').toUpperCase()
    if (status === 'QA_FAILED') return { label: 'FIX', color: '#ef4444' }
    if (status === 'PROCESSING' || status === 'RUNNING') return { label: 'WORK', color: '#00d4ff' }
    if (status === 'QA') return { label: 'QA', color: '#f59e0b' }
    if (status === 'COMPLETED') return { label: 'DONE', color: '#00ff88' }
    if (status === 'PENDING' || status === 'QUEUED') return { label: 'WAIT', color: '#8b5cf6' }
    return { label: status.slice(0, 4), color: '#888' }
  }

  // Render a task card
  const renderTaskCard = (task: Task & { fileType?: FileType }, stageColor: string, index: number) => {
    const taskId = getTaskId(task)
    const isLanded = justLandedIds.has(taskId)
    const statusBadge = getStatusBadge(task)
    const description = task.description || task.name || 'No description'
    const shortDesc = description.length > 60 ? description.slice(0, 60) + '…' : description

    return (
      <div
        key={taskId}
        className={`task-card ${selectedTaskId === taskId ? 'selected' : ''} ${isLanded ? 'just-landed' : ''}`}
        style={{
          '--i': index,
          '--stage-color': stageColor
        } as React.CSSProperties}
        onClick={() => handleTaskClick(taskId)}
        title={description}
      >
        <div className="task-header">
          <span className="status-badge" style={{ background: statusBadge.color }}>{statusBadge.label}</span>
          <span className="task-id">#{taskId.slice(0, 6)}</span>
        </div>
        <div className="task-description">{shortDesc}</div>
        {task.progress > 0 && task.progress < 100 && (
          <div className="task-progress">
            <div className="progress-fill" style={{ width: `${task.progress}%` }} />
          </div>
        )}
      </div>
    )
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className={`assembly-container ${compact ? 'compact' : ''}`}>
      {/* Header with inline task input */}
      <div className="assembly-header">
        <div className="assembly-title">
          <span className="title-icon">🔥</span>
          <span>Pipeline</span>
          <span className={`status-dot ${isRunning ? 'running' : ''}`} />
        </div>

        {/* Inline task input - hidden in compact mode */}
        {!compact && (
          <div className="assembly-input">
            <input
              type="text"
              placeholder="New task..."
              value={taskInput}
              onChange={(e) => setTaskInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmitTask()}
              disabled={isSubmitting}
            />
            <button
              onClick={handleSubmitTask}
              disabled={!taskInput.trim() || isSubmitting}
              className={taskInput.trim() ? 'active' : ''}
            >
              <Send className={`w-4 h-4 ${isSubmitting ? 'animate-pulse' : ''}`} />
            </button>
          </div>
        )}

        <div className="assembly-stats">
          <span>{tasks.length} tasks</span>
        </div>
      </div>

      {/* Dumb Pipe - Single continuous flow */}
      {loading ? (
        <div className="assembly-loading"><div className="loader" /></div>
      ) : error ? (
        <div className="assembly-error">⚠️ {error}</div>
      ) : (
        <div className="pipeline-row dumb-pipe">
          <div className="pipe-interior">
            {tasks.length === 0 ? (
              <div className="empty-state">No tasks in pipe</div>
            ) : (
              <div className="pipe-tasks">
                {tasks.slice(0, 12).map((task, i) => renderTaskCard(task, '#ff8c32', i))}
                {tasks.length > 12 && (
                  <div className="overflow-text">+{tasks.length - 12} more</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Expanded Task View */}
      {expandedTaskId && (
        <div className="task-expanded-overlay" onClick={() => setExpandedTaskId(null)}>
          <div className="task-expanded-content" onClick={(e) => e.stopPropagation()}>
            <div className="task-expanded-header">
              <h3>Task: {expandedTaskId.slice(0, 8)}</h3>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  onClick={() => setLiveLogsEnabled(!liveLogsEnabled)}
                  style={{
                    padding: '4px 12px',
                    background: liveLogsEnabled ? 'rgba(255, 140, 50, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                    border: `1px solid ${liveLogsEnabled ? '#ff8c32' : 'rgba(255, 255, 255, 0.3)'}`,
                    color: liveLogsEnabled ? '#ff8c32' : '#fff',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontFamily: 'monospace'
                  }}
                >
                  {liveLogsEnabled ? '● LIVE' : '○ Static'}
                </button>
                <button className="close-btn" onClick={() => setExpandedTaskId(null)}>✕</button>
              </div>
            </div>

            {/* Task Status Bar */}
            {taskStatus && (
              <div className="task-status-bar" style={{
                padding: '12px 16px',
                background: 'rgba(255, 140, 50, 0.1)',
                borderBottom: '1px solid rgba(255, 140, 50, 0.3)',
                fontFamily: 'monospace',
                fontSize: '13px'
              }}>
                <div style={{ display: 'flex', gap: '20px', marginBottom: '8px' }}>
                  <div><strong>Status:</strong> {taskStatus.status}</div>
                  <div><strong>Progress:</strong> {taskStatus.progress}%</div>
                </div>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div><strong>Tokens:</strong> {taskStatus.tokens_used.toLocaleString()}</div>
                  {taskStatus.elapsed_seconds && (
                    <div><strong>Elapsed:</strong> {Math.floor(taskStatus.elapsed_seconds)}s</div>
                  )}
                  {taskStatus.files_modified.length > 0 && (
                    <div><strong>Files:</strong> {taskStatus.files_modified.length}</div>
                  )}
                </div>
              </div>
            )}

            <div className="task-expanded-output">
              <pre>{taskOutput || 'Loading...'}</pre>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="assembly-footer">
        <span>Live • {tasks.length} total</span>
        <span>{new Date().toLocaleTimeString()}</span>
      </div>
    </div>
  )
}

export default AssemblyLine
